import { configMgmtControllerFindRequestType } from '@kalba/api-client';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { ChevronRight, FileCheck, Home } from 'lucide-react';
import type { ReactNode } from 'react';
import { AppLayout } from '../../../../../components/layout/app-layout';
import { cn } from '../../../../../lib/utils';
import type { PermitTypeDetail } from './types';

const TABS = [
  { label: 'General', path: '' },
  { label: 'Categories', path: '/risks' },
  { label: 'Document Types', path: '/documents' },
] as const;

interface Props {
  requestTypeId: string;
  children: (permitType: PermitTypeDetail) => ReactNode;
}

export function PermitTypeShell({ requestTypeId, children }: Props) {
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;
  const navigate = useNavigate();

  const {
    data: permitType,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['permitType', requestTypeId],
    queryFn: () =>
      configMgmtControllerFindRequestType({
        id: requestTypeId,
      }) as unknown as Promise<PermitTypeDetail>,
  });

  const baseUrl = `/config/request-types/${requestTypeId}`;

  function getTabHref(tabPath: string) {
    return tabPath === '' ? baseUrl : `${baseUrl}${tabPath}`;
  }

  function isTabActive(tabPath: string) {
    const href = getTabHref(tabPath);
    if (tabPath === '') return currentPath === href || currentPath === `${href}/`;
    return currentPath.startsWith(href);
  }

  return (
    <AppLayout>
      <nav className="mb-5 flex items-center gap-1.5 text-xs text-gray-400">
        <button
          type="button"
          onClick={() => navigate({ to: '/dashboard' })}
          className="shrink-0 transition-colors hover:text-gray-600"
        >
          <Home size={12} />
        </button>
        <ChevronRight size={12} className="shrink-0" />
        <button
          type="button"
          onClick={() => navigate({ to: '/config/request-types' })}
          className="transition-colors hover:text-gray-600 font-medium"
        >
          Request Types
        </button>
        <ChevronRight size={12} className="shrink-0" />
        <span className="font-medium text-gray-600">{permitType?.name ?? '…'}</span>
      </nav>
      <div className="space-y-5">
        {/* Loading skeleton */}
        {isLoading && (
          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
            <div className="h-1 bg-gray-100 animate-pulse" />
            <div className="p-6">
              <div className="flex items-center gap-4">
                <div className="h-[72px] w-[72px] rounded-2xl bg-gray-100 animate-pulse" />
                <div className="space-y-2 flex-1">
                  <div className="h-5 w-48 rounded bg-gray-100 animate-pulse" />
                  <div className="h-3.5 w-32 rounded bg-gray-100 animate-pulse" />
                </div>
              </div>
            </div>
          </div>
        )}

        {isError && (
          <div className="rounded-2xl border border-error-200 bg-error-50 p-6">
            <p className="text-sm text-error-600">Failed to load request type.</p>
          </div>
        )}

        {permitType && (
          <>
            {/* Header card */}
            <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
              <div className="h-1 bg-gradient-to-r from-blue-400 via-indigo-500 to-violet-400" />
              <div className="px-6 py-5">
                <div className="flex items-center gap-4">
                  {/* Avatar */}
                  <div className="flex h-[64px] w-[64px] shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 shadow-md">
                    <FileCheck size={28} className="text-white" />
                  </div>

                  {/* Meta */}
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2.5 mb-1">
                      <h1 className="text-xl font-bold text-gray-900">{permitType.name}</h1>
                      <span className="rounded-lg border border-gray-200 bg-gray-50 px-2 py-0.5 font-mono text-xs font-semibold text-gray-500">
                        {permitType.code}
                      </span>
                      <span
                        className={cn(
                          'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold',
                          permitType.status === 'ACTIVE'
                            ? 'bg-success-50 border border-success-200 text-success-700'
                            : 'bg-gray-100 border border-gray-200 text-gray-500',
                        )}
                      >
                        {permitType.status === 'ACTIVE' ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    {(permitType as unknown as { description?: string | null }).description && (
                      <p className="text-sm text-gray-500">
                        {(permitType as unknown as { description: string }).description}
                      </p>
                    )}

                    {/* Feature flags strip */}
                    <div className="mt-2.5 flex flex-wrap items-center gap-2">
                      {permitType.riskLevels?.length > 0 && (
                        <span className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-0.5 text-[10px] font-semibold text-gray-500">
                          {permitType.riskLevels.length} Categories
                        </span>
                      )}
                      {permitType.feeComponents?.length > 0 && (
                        <span className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-0.5 text-[10px] font-semibold text-gray-500">
                          {permitType.feeComponents.length} Fee Components
                        </span>
                      )}
                      {permitType.documentTypeLinks?.length > 0 && (
                        <span className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-0.5 text-[10px] font-semibold text-gray-500">
                          {permitType.documentTypeLinks.length} Document Types
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Tab bar */}
            <div className="flex gap-1.5 overflow-x-auto rounded-2xl border border-gray-200 bg-white p-1.5 shadow-sm">
              {TABS.map((tab) => {
                const active = isTabActive(tab.path);
                return (
                  <Link
                    key={tab.path}
                    to={getTabHref(tab.path) as string}
                    className={cn(
                      'shrink-0 rounded-xl px-4 py-2.5 text-sm font-semibold transition-all duration-150 whitespace-nowrap',
                      active
                        ? 'bg-success-500 text-white shadow-sm'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50',
                    )}
                  >
                    {tab.label}
                  </Link>
                );
              })}
            </div>

            {/* Tab content */}
            {children(permitType)}
          </>
        )}
      </div>
    </AppLayout>
  );
}
